// import 'https://js-eu1.hsforms.net/forms/embed/v3.js'
// import 'https://js-eu1.hsforms.net/forms/embed/v2.js'
import { Block } from '@/core/Block'
import { Sleep } from '@/utils/Sleep'
import { Button } from '@/components/templates/Button'
import { ButtonRound } from '@/components/templates/ButtonRound'
import { Events, EventType } from '@/utils/Events'

export class Form extends Block {
  static hubspotLoaderPromise = null
  static hubspotExternalScriptPromise = null

  init() {
    super.init()

    this.formId = this.el.dataset.formId
    this.portalId = this.el.dataset.portalId
    this.modalId = this.el.dataset.modalId
    this.moduleId = this.el.dataset.moduleId

    this.inputs = []

    this.onFileInputChange = this.onFileInputChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onSubmitted = this.onSubmitted.bind(this)
    this.onModuleFormReset = this.onModuleFormReset.bind(this)
    this.onModalShow = this.onModalShow.bind(this)

    this.modal = document.querySelector(`.c-modal[data-modal="${this.modalId}"]`)
    this.newsletter = this.el.closest('.c-section--newsletter')
    this.formOne = this.el.closest('.c-section--form-1')

    this.isInModal = this.modal !== null
    this.isNewsletter = this.newsletter !== null
    this.isFormOne = this.formOne !== null
    this.isModuleForm = this.moduleId !== null
    this.formObserver = null
    this.formInitPromise = null
    this.formInitialized = false
    this.lazyInitBound = false
    this.targetId = this.ensureUniqueTargetId()

    this.onFocusIn = this.onFocusIn.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onTouchStart = this.onTouchStart.bind(this)

    if (this.isInModal) {
      this.successPage = this.modal.querySelector('.c-modal-success')
    }
  }

  async addEvents() {
    super.addEvents()
    this.setupLazyFormInit()
    Events.on(EventType.MODAL_SHOW, this.onModalShow)
    Events.on(EventType.MODULE_FORM_RESET, this.onModuleFormReset)
  }

  setupLazyFormInit() {
    if (this.lazyInitBound) return
    this.lazyInitBound = true

    this.el.addEventListener('focusin', this.onFocusIn)
    this.el.addEventListener('pointerdown', this.onPointerDown, { passive: true })
    this.el.addEventListener('touchstart', this.onTouchStart, { passive: true })

    const nearViewport = this.isNearViewport()

    if ('IntersectionObserver' in window) {
      this.formObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.initHubspotForm()
            }
          })
        },
        {
          rootMargin: '250px 0px',
          threshold: 0,
        }
      )
      this.formObserver.observe(this.el)
    }

    if (nearViewport || !('IntersectionObserver' in window)) {
      this.initHubspotForm()
    }

    // Keep inline forms reliable: initialize immediately when the container is visible.
    if (this.isElementVisible()) {
      this.initHubspotForm()
    }
  }

  isNearViewport(offset = 250) {
    if (!this.el || !this.el.isConnected) return false

    const style = window.getComputedStyle(this.el)
    if (style.display === 'none' || style.visibility === 'hidden') return false

    const rect = this.el.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return false
    return rect.top <= window.innerHeight + offset && rect.bottom >= -offset
  }

  isElementVisible() {
    if (!this.el || !this.el.isConnected) return false
    const style = window.getComputedStyle(this.el)
    return style.display !== 'none' && style.visibility !== 'hidden'
  }

  async initHubspotForm(force = false) {
    if (this.formInitialized && !force) return
    if (this.formInitPromise && !force) return this.formInitPromise

    this.formInitPromise = (async () => {
      await Form.ensureHubspotLoaded()
      if (!window.hbspt?.forms?.create) {
        throw new Error('HubSpot forms API is not available')
      }

      let targetRoot = this.el.querySelector('.hbspt-form')
      if (!targetRoot) {
        targetRoot = document.createElement('div')
        targetRoot.className = 'hbspt-form'
        this.el.appendChild(targetRoot)
      }

      if (force) {
        targetRoot.innerHTML = ''
      }

      this.formInstance = hbspt.forms.create({
        portalId: this.portalId,
        formId: this.formId,
        target: `#${this.targetId} .hbspt-form`,
        onFormReady: (e) => this.setupInputs(e),
        onFormSubmit: (e) => this.onSubmit(e),
        onFormSubmitted: (e) => this.onSubmitted(e, this.formInstance.id),
      })

      this.formInitialized = true
      if (this.formObserver) this.formObserver.disconnect()
    })().catch((error) => {
      this.formInitialized = false
      this.formInitPromise = null
      console.warn(`Failed to initialize HubSpot form ${this.formId}:`, error)
      return null
    })

    return this.formInitPromise
  }

  ensureUniqueTargetId() {
    const baseId = this.el.id || `hbspt_form_${this.formId || 'instance'}`
    const uniqueId = `${baseId}__${this.id}`
    this.el.id = uniqueId
    return uniqueId
  }

  static async ensureHubspotLoaded() {
    if (window.hbspt?.forms?.create) return

    if (!Form.hubspotLoaderPromise) {
      Form.hubspotLoaderPromise = import('@vendor/hbspt_v2').catch(() => null)
    }

    await Form.hubspotLoaderPromise

    if (window.hbspt?.forms?.create) return

    await Form.loadHubspotFallbackScript()
  }

  static loadHubspotFallbackScript() {
    if (window.hbspt?.forms?.create) return Promise.resolve()
    if (Form.hubspotExternalScriptPromise) return Form.hubspotExternalScriptPromise

    Form.hubspotExternalScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-hubspot-fallback="true"]')
      if (existingScript) {
        if (window.hbspt?.forms?.create) {
          resolve()
          return
        }

        existingScript.addEventListener('load', () => {
          if (window.hbspt?.forms?.create) {
            resolve()
          } else {
            reject(new Error('HubSpot fallback script loaded but API is unavailable'))
          }
        }, { once: true })
        existingScript.addEventListener('error', () => reject(new Error('HubSpot fallback script failed to load')), { once: true })
        return
      }

      const script = document.createElement('script')
      script.src = 'https://js-eu1.hsforms.net/forms/embed/v2.js'
      script.async = true
      script.defer = true
      script.dataset.hubspotFallback = 'true'
      script.onload = () => {
        if (window.hbspt?.forms?.create) {
          resolve()
        } else {
          reject(new Error('HubSpot fallback script loaded but API is unavailable'))
        }
      }
      script.onerror = () => reject(new Error('HubSpot fallback script failed to load'))
      document.head.appendChild(script)
    }).catch((error) => {
      Form.hubspotExternalScriptPromise = null
      throw error
    })

    return Form.hubspotExternalScriptPromise
  }

  onFocusIn() {
    this.initHubspotForm()
  }

  onPointerDown() {
    this.initHubspotForm()
  }

  onTouchStart() {
    this.initHubspotForm()
  }

  onModalShow(modalId) {
    if (!modalId || !this.modalId || modalId !== this.modalId) return
    this.initHubspotForm()
  }

  async onModuleFormReset(moduleId) {
    if (this.moduleId && this.moduleId === moduleId) {
      this.formInitialized = false
      this.formInitPromise = null
      await this.initHubspotForm(true)
    }
  }

  onSubmitted(e, formId) {
    if (this.isInModal) {
      const duplicateFormModals = document.querySelectorAll(`[data-modal-form-id="${formId}"]`)
      duplicateFormModals.forEach((modal) => {
        modal.scrollTo({ top: 0 })
        modal.classList.add('is-success')
        modal.querySelector('.c-modal-success')?.classList.add('is-active')
      })
    }

    if (this.newsletter) {
      Events.emit(EventType.MODAL_SHOW, 'newsletter-success')
      this.successModal = document.querySelector('[data-modal="newsletter-success"]')
      this.successModal?.classList.add('is-success')
      this.successModal.querySelector('.c-modal-success')?.classList.add('is-active')

      this.formInstance = null
      this.addEvents()
    }

    if (this.formOne) {
      Events.emit(EventType.MODAL_SHOW, 'form-1-success')
      this.successModal = document.querySelector('[data-modal="form-1-success"]')
      this.successModal?.classList.add('is-success')
      this.successModal.querySelector('.c-modal-success')?.classList.add('is-active')

      this.formInstance = null
      this.addEvents()
    }

    if (this.isModuleForm) {
      const successModalId = `module-${this.moduleId}-form-success`
      Events.emit(EventType.MODAL_SHOW, successModalId)
      this.successModal = document.querySelector(`[data-modal="${successModalId}"]`)
      this.successModal?.classList.add('is-success')
      this.successModal.querySelector('.c-modal-success')?.classList.add('is-active')

      const duplicateFormModules = document.querySelectorAll(`[data-form-id="${formId}"][data-module-id]`)
      duplicateFormModules.forEach((module) => {
        Events.emit(EventType.MODULE_FORM_RESET, module.dataset.moduleId)
      })
    }
  }

  setupInputs(e) {
    this.inputs =
      [
        ...this.el.querySelectorAll('.hs-fieldtype-phonenumber'),
        ...this.el.querySelectorAll('.hs-fieldtype-text'),
        ...this.el.querySelectorAll('.hs-fieldtype-textarea'),
        ...this.el.querySelectorAll('.hs-fieldtype-file'),
      ] || []

    this.clearInputs(e)
    this.inputs.forEach((el) => {
      this.addInputEvents(el)
    })

    const timestamp = Date.now()
    this.idInputs = this.el.querySelectorAll('input[id]')
    this.forLabels = this.el.querySelectorAll('label[for]')
    this.idInputs.forEach((el) => (el.id += `_${timestamp}`))
    this.forLabels.forEach((el) => el.setAttribute('for', el.getAttribute('for') + `_${timestamp}`))

    this.formatSubmitButton()
    this.submitButton = this.el.querySelector('.hs-button')
    if (this.submitButton) this.submitButton.addEventListener('click', this.onSubmit)
  }

  async destroy() {
    super.destroy()

    this.el.removeEventListener('focusin', this.onFocusIn)
    this.el.removeEventListener('pointerdown', this.onPointerDown)
    this.el.removeEventListener('touchstart', this.onTouchStart)
    Events.off(EventType.MODAL_SHOW, this.onModalShow)
    Events.off(EventType.MODULE_FORM_RESET, this.onModuleFormReset)
    if (this.formObserver) this.formObserver.disconnect()
    this.lazyInitBound = false

    this.inputs.forEach((el) => {
      this.removeEventsInput(el)
    })

    if (typeof HubSpotForms !== 'undefined' && HubSpotForms?.removeAll) {
      HubSpotForms.removeAll()
    }
  }

  async onSubmit(e) {
    await Sleep()

    const fileInputs = this.el.querySelectorAll('.hs-fieldtype-file input[type="file"]')

    fileInputs.forEach((input) => {
      this.validateFileInput(input)
    })
  }

  clearInputs(form) {
    if (!form || form.tagName.toLowerCase() !== 'form') return
    const inputs = form.querySelectorAll('.hs-input')
    inputs.forEach((input) => {
      if (input.tagName.toLowerCase() === 'select') return
      if (input.value && input.closest('.hs-form-field')) {
        input.closest('.hs-form-field').classList.add('is-active')
      }
    })
  }

  removeEventsInput(el) {
    const input = el.querySelector('.input input')
    const textarea = el.querySelector('.input textarea')

    if (input) {
      input.removeEventListener('focus', this.setActiveInput.bind(this, el))
      input.removeEventListener('blur', this.removeActiveInput.bind(this, el))
    }

    if (textarea) {
      textarea.removeEventListener('focus', this.setActiveInput.bind(this, el))
      textarea.removeEventListener('blur', this.removeActiveInput.bind(this, el))
    }
  }

  formatSubmitButton() {
    const button = this.el.querySelector('.hs-submit .hs-button')

    if (!button) return

    const wrapper = document.createElement('div')

    if (this.isNewsletter) {
      wrapper.classList.add('c-button--round')
      wrapper.innerHTML = ButtonRound({ text: button.value })
    } else {
      wrapper.classList.add('c-button')
      wrapper.classList.add('c-button--icon-hover')
      wrapper.classList.add('c-button--ts-2')
      wrapper.innerHTML = Button({ text: button.value })
    }

    button.parentNode.insertBefore(wrapper, button)
    wrapper.appendChild(button)
  }

  addInputEvents(el) {
    const input = el.querySelector('.input input')
    const textarea = el.querySelector('.input textarea')
    const fileInput = el.querySelector('.input input[type="file"]')

    if (input && !fileInput) {
      input.addEventListener('focus', this.setActiveInput.bind(this, el))
      input.addEventListener('blur', this.removeActiveInput.bind(this, el))
    }

    if (textarea) {
      textarea.addEventListener('focus', this.setActiveInput.bind(this, el))
      textarea.addEventListener('blur', this.removeActiveInput.bind(this, el))
    }

    if (fileInput) {
      fileInput.addEventListener('change', this.onFileInputChange)
    }
  }

  setActiveInput(el) {
    el.classList.add('is-active')
  }

  removeActiveInput(el) {
    const input = el.querySelector('.input input')
    const textarea = el.querySelector('.input textarea')

    if (input && !input.value) {
      el.classList.remove('is-active')
    }

    if (textarea && !textarea.value) {
      el.classList.remove('is-active')
    }
  }

  async onFileInputChange(e) {
    let fileName = ''

    const id = e.target.id
    const label = document.querySelector(`label[for="${id}"]`)
    const wrapper = e.target.closest('.hs-fieldtype-file')

    fileName = e.target.value.split('\\').pop()

    if (fileName) {
      wrapper.classList.add('is-filled')
      label.querySelector('span').innerHTML = fileName
    } else {
      label.innerHTML = labelVal
    }

    await Sleep(100)

    this.validateFileInput(e.target)
  }

  validateFileInput(input) {
    const wrapper = input.closest('.hs-fieldtype-file')
    const errorMessage = wrapper.querySelector('.hs-error-msg')
    const description = wrapper.querySelector('.hs-field-desc')

    wrapper.classList.remove('is-error')

    if (errorMessage) {
      if (description) {
        description.style.display = 'block'
        description.innerText = errorMessage.innerText
      }

      wrapper.classList.add('is-error')
      wrapper.classList.remove('is-filled')
    }
  }
}
